// notizSeite.ts — pure Helfer der Termin-Notiz-Seite im OneNote-Stil
// (Termine-SP7/A12, Route /termine/notizen/:terminId).
//
// Bewusst OHNE React/DOM (wie peek.ts/terminBlock.ts), damit die Logik unter
// vitest (node-Env, nur *.test.ts) testbar bleibt: Drawing-Normalisierung
// (Server-jsonb defensiv einlesen), Radierer-Hit-Test (Punkt↔Segment-Abstand,
// ganze Strokes löschbar — Zeichnen-MVP, Entscheidung 66) und die
// Flächen-Höhe, damit die Zeichnung nie unter dem Seitenende abgeschnitten
// wird. Die Komponente lebt in TerminNotizenPage.tsx.

import type { NotizDrawingDto, NotizStrokeDto } from "../../services/calendarTerminService";

export type NotizPunkt = [number, number];

/** Stift-Presets (Zeichnen-MVP, Entscheidung 66: 3 Stiftfarben + Radierer). */
export const NOTIZ_STIFT_FARBEN: { hex: string; label: string }[] = [
    { hex: "#7d2169", label: "Aubergine" },
    { hex: "#3a6ea5", label: "Blau" },
    { hex: "#c23b3b", label: "Rot" },
];

/** Standard-Strichbreite (CSS-Pixel) des Freihand-Stifts. */
export const NOTIZ_STIFT_BREITE = 2.5;

/** Treffer-Radius des Radierers in CSS-Pixeln (Finger-/Pencil-tauglich). */
export const RADIERER_RADIUS = 12;

let strokeZaehler = 0;

/** Eindeutige Stroke-Id (Zeit + Laufindex — kollisionsfrei genug je Seite). */
export function neueStrokeId(): string {
    strokeZaehler = (strokeZaehler + 1) % 1_000_000;
    return `s${Date.now().toString(36)}-${strokeZaehler}`;
}

/**
 * Liest das Server-Drawing (jsonb → unknown) DEFENSIV ein: nur wohlgeformte
 * Strokes (id, Farbe, endliche Zahlenpaare) überleben, alles andere wird still
 * verworfen — eine kaputte Zeile in der DB darf die Seite nie crashen.
 */
export function normalizeDrawing(raw: unknown): NotizDrawingDto {
    const leer: NotizDrawingDto = { strokes: [] };
    if (raw == null || typeof raw !== "object") return leer;
    const strokesRaw = (raw as { strokes?: unknown }).strokes;
    if (!Array.isArray(strokesRaw)) return leer;

    const strokes: NotizStrokeDto[] = [];
    for (const s of strokesRaw) {
        if (s == null || typeof s !== "object") continue;
        const cand = s as { id?: unknown; color?: unknown; width?: unknown; points?: unknown };
        if (typeof cand.id !== "string" || cand.id.length === 0) continue;
        if (!Array.isArray(cand.points)) continue;
        const points: NotizPunkt[] = [];
        for (const p of cand.points) {
            if (!Array.isArray(p) || p.length < 2) continue;
            const x = Number(p[0]);
            const y = Number(p[1]);
            if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
            points.push([x, y]);
        }
        if (points.length === 0) continue;
        const width = Number(cand.width);
        strokes.push({
            id: cand.id,
            color: typeof cand.color === "string" && cand.color.trim().length > 0
                ? cand.color.trim()
                : NOTIZ_STIFT_FARBEN[0].hex,
            width: Number.isFinite(width) && width > 0 ? width : NOTIZ_STIFT_BREITE,
            points,
        });
    }
    return { strokes };
}

/** Kürzester Abstand eines Punkts zum Liniensegment a–b (Radierer-Hit-Test). */
export function punktSegmentAbstand(p: NotizPunkt, a: NotizPunkt, b: NotizPunkt): number {
    const dx = b[0] - a[0];
    const dy = b[1] - a[1];
    const lenSq = dx * dx + dy * dy;
    // Entartetes Segment (Einzelpunkt): Abstand zum Punkt a.
    let t = 0;
    if (lenSq > 0) {
        t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / lenSq;
        t = Math.max(0, Math.min(1, t));
    }
    const nx = a[0] + t * dx;
    const ny = a[1] + t * dy;
    return Math.hypot(p[0] - nx, p[1] - ny);
}

/** Trifft der Radierer-Punkt (mit Radius) diesen Stroke? */
export function strokeGetroffen(
    stroke: Pick<NotizStrokeDto, "points" | "width">,
    p: NotizPunkt,
    radius: number = RADIERER_RADIUS,
): boolean {
    const pts = stroke.points;
    if (pts.length === 0) return false;
    // Strichbreite zählt zur Trefferfläche dazu (dicke Striche leichter treffen).
    const schwelle = radius + stroke.width / 2;
    if (pts.length === 1) return Math.hypot(p[0] - pts[0][0], p[1] - pts[0][1]) <= schwelle;
    for (let i = 0; i < pts.length - 1; i++) {
        if (punktSegmentAbstand(p, pts[i], pts[i + 1]) <= schwelle) return true;
    }
    return false;
}

/**
 * Radierer-Anwendung: entfernt ALLE Strokes, die der Punkt trifft (MVP:
 * Strich-weises Löschen, kein Teil-Radieren). Liefert das (ggf. unveränderte)
 * Drawing plus Flag, ob etwas entfernt wurde — der Aufrufer speichert nur bei
 * `entfernt=true` und behält sonst die Objekt-Identität (kein Re-Render).
 */
export function radiereAnPunkt(
    drawing: NotizDrawingDto,
    p: NotizPunkt,
    radius: number = RADIERER_RADIUS,
): { drawing: NotizDrawingDto; entfernt: boolean } {
    const rest = drawing.strokes.filter((s) => !strokeGetroffen(s, p, radius));
    if (rest.length === drawing.strokes.length) return { drawing, entfernt: false };
    return { drawing: { strokes: rest }, entfernt: true };
}

/**
 * Nötige Mindest-Höhe der Notiz-Fläche in CSS-Pixeln: unterster Zeichen-Punkt
 * + Luft, mindestens `minHoehe` — die Zeichnung darf nie unterhalb der Fläche
 * abgeschnitten werden, auch wenn der Text kürzer ist.
 */
export function zeichnungsHoehe(drawing: NotizDrawingDto, minHoehe: number): number {
    let maxY = 0;
    for (const s of drawing.strokes) {
        for (const p of s.points) {
            if (p[1] > maxY) maxY = p[1];
        }
    }
    return Math.max(minHoehe, Math.ceil(maxY + 80));
}
